import * as React from 'react';

import { useQuery } from '@tanstack/react-query';
import { groupBy, toPairs } from 'lodash-es';
import { useSetRecoilState } from 'recoil';

import type { Shops } from './Swagger';
import axiosInstance from './axios/axios';
import useAxiosGetOnLoad from './axios/useAxiosGetOnLoad';
import type { Metadata2 } from './state/metadataState';
import { metadata2State, metadataState } from './state/metadataState';
import { sizeTypesMetadataState } from './state/sizeTypesMetadataState';

interface MetadataType {
  code: string;
  domain: string;
  subDomain: string;
  description: {
    [key in 'ko' | 'en' | 'fr' | 'es' | 'ru']: string;
  };
}

interface MetadataResponse {
  count: number;
  domains: string[];
  subDomains: string[];
  items: MetadataType[];
}

interface MetadataRequest {
  domain?: string;
  subDomain?: string;
}

/**
 * Metadata를 fetch하고 정제합니다.
 */
export default function MetadataProvider() {
  const setMetadata2 = useSetRecoilState(metadata2State);
  const setMetadata = useSetRecoilState(metadataState);
  const setSizeTypesMetadata = useSetRecoilState(sizeTypesMetadataState);

  const [sizeMetaData] = useAxiosGetOnLoad<Shops.MetadataCategorySizeTypesList.ResponseBody>(
    axiosInstance,
    '/shops/metadata/category-size-types',
    {
      domain: 'shop',
    },
  );

  const [shopMetaData] = useAxiosGetOnLoad<MetadataResponse, MetadataRequest>(axiosInstance, '/shops/metadata', {
    domain: 'shop',
  });

  const { data: metadataList } = useQuery({
    queryKey: ['metadata'],
    queryFn: () => axiosInstance.get<MetadataResponse>('/shops/metadata').then(v => v.data),
    staleTime: 1000 * 60 * 60 * 24,
    gcTime: 1000 * 60 * 60 * 24,
    refetchOnWindowFocus: false,
  });

  React.useEffect(() => {
    if (metadataList && sizeMetaData) {
      setSizeTypesMetadata(sizeMetaData.properties ?? []);
      setMetadata(shopMetaData?.items ?? []);

      let convertedMetadata: Metadata2 = {};

      toPairs(groupBy(metadataList.items, 'domain')).forEach(([key, value]) => {
        let subDomainMapping: Metadata2[string] = {};

        toPairs(groupBy(value, 'subDomain')).forEach(([key, value]) => {
          let codeMapping: Record<string, string> = {};

          toPairs(groupBy(value, 'code')).forEach(([key, value]) => {
            codeMapping = {
              ...codeMapping,
              [key]: value[0]?.description.ko ?? '',
            };
          });

          subDomainMapping = {
            ...subDomainMapping,
            [key]: {
              codeList: value.map(v => v.code),
              ...codeMapping,
            } as Metadata2[string][string],
          };
        });

        convertedMetadata = {
          ...convertedMetadata,
          [key]: subDomainMapping,
        };
      });

      setMetadata2(convertedMetadata);
    }
  }, [
    sizeMetaData?.properties,
    setSizeTypesMetadata,
    metadataList,
    setMetadata2,
    sizeMetaData,
    setMetadata,
    shopMetaData?.items,
  ]);

  return null;
}
